// The presenter's notes: N on the laptop, always open in the presenter
// window and on the phone.

// A node's words as they read: a line break in a headline is a space, not
// two words run together.
export function plainText(node) {
  const copy = node.cloneNode(true);
  copy.querySelectorAll("br").forEach((br) => br.replaceWith(" "));
  return copy.textContent.replace(/\s+/g, " ").trim();
}

// What a board is headed by, whichever way it is drawn: the board's
// headline, a cover's or a sentence's, a spread's or a moodboard's title, a
// chapter opener's or a front page's name.
export const HEADLINE = ".deck-headline, .deck-cover-title, .deck-statement, .deck-spread-title, .deck-moodboard-title, .deck-opener-title";

// A chapter's opening page goes with its chapter, and a board's front page
// with its board (hiding.js): neither is chosen, hidden or ordered alone.
export const attached = (board) => "opener" in board.dataset || "front" in board.dataset;

export default {
  toggleNotes() {
    this.notesOpen = !this.notesOpen;
    this.renderNotes();
  },

  // The presenter's notes for the board on show: its headline, Saga's read
  // and its list, then the board after it.
  renderNotes() {
    if (!this.hasNotesPanelTarget) return;
    this.notesPanelTarget.hidden = !this.notesOpen;
    if (this.hasNotesButtonTarget) this.notesButtonTarget.setAttribute("aria-pressed", String(this.notesOpen));
    if (!this.notesOpen) return;

    this.renderOwnNote();

    const board = this.slideTargets[this.indexValue];
    const text = (selector) => Array.from(board?.querySelectorAll(selector) || []).map(plainText).filter(Boolean);
    const headline = text(HEADLINE)[0] || "";
    const read = text(".deck-standfirst, .deck-lede");
    // A board that is one figure has no headline: say the figure.
    const figures = text(".deck-figure-hero, .deck-figure-xl, .deck-figure-l, .deck-figure-m").slice(0, 4);
    if (figures.length) read.unshift(figures.join(" · "));
    const items = text(".deck-list .deck-prose, .deck-list > div > div > div:first-child").slice(0, 6);
    // Set as text, never parsed: plainText undoes the escaping the page was
    // drawn with, so a product title or a Klaviyo campaign name written as
    // markup ran in the presenter's session when this was innerHTML.
    const node = (tag, words) => {
      const element = document.createElement(tag);
      element.textContent = words;
      return element;
    };
    const parts = [];
    if (headline) parts.push(node("strong", headline));
    read.forEach((r) => parts.push(node("p", r)));
    if (items.length) {
      const list = document.createElement("ul");
      list.append(...items.map((i) => node("li", i)));
      parts.push(list);
    }
    this.notesTarget.replaceChildren(...parts);

    // What is behind the board, for the presenter to answer from without
    // turning the room's board over.
    if (this.hasNotesBackTarget) {
      const back = this.backOf();
      this.notesBackTarget.hidden = !back;
      const body = this.notesBackTarget.querySelector(".deck-notes-back-body");
      if (body) body.innerHTML = back ? back.querySelector(".deck-back-body")?.innerHTML || "" : "";
    }

    const after = this.visibleIndices().find((i) => i > this.indexValue);
    const nextBoard = after !== undefined ? this.slideTargets[after] : null;
    const nextHeadline = nextBoard?.querySelector(HEADLINE);
    const nextTitle = nextBoard ? (nextHeadline ? plainText(nextHeadline) : "") || nextBoard.dataset.slideSlug : "";
    if (this.hasNextUpTarget) this.nextUpTarget.textContent = nextTitle ? `${this.element.dataset.presentationNextLabel}: ${nextTitle}` : "";
  },

  // The presenter's own note on the board on show, above Saga's: read
  // here, written here when this window may (the owner's, or the phone).
  renderOwnNote() {
    if (!this.hasOwnNoteTarget) return;
    const slug = this.slugByIndex[this.indexValue];
    // The page turned while a note was open: keep what was written, and
    // close it — the note was for the board that has gone.
    if (this.editingSlug && this.editingSlug !== slug) this.closeNote();

    const note = (slug && this.notesBySlug[slug]) || "";
    const editable = this.hasOwnNoteInputTarget && Boolean(slug);
    const editing = this.editingSlug === slug && Boolean(slug);
    this.ownNoteTarget.hidden = !note && !editable;
    this.ownNoteTextTarget.textContent = note;
    this.ownNoteTextTarget.hidden = !note || editing;
    if (this.hasOwnNoteInputTarget) this.ownNoteInputTarget.hidden = !editing;
    if (this.hasOwnNoteButtonTarget) {
      const button = this.ownNoteButtonTarget;
      button.hidden = !editable;
      button.textContent = editing ? button.dataset.doneLabel : note ? button.dataset.editLabel : button.dataset.addLabel;
    }
  },

  editNote() {
    const slug = this.slugByIndex[this.indexValue];
    if (!slug) return;
    if (this.editingSlug === slug) {
      this.closeNote();
    } else {
      this.editingSlug = slug;
      this.ownNoteInputTarget.value = this.notesBySlug[slug] || "";
    }
    this.renderOwnNote();
    if (this.editingSlug) this.ownNoteInputTarget.focus();
  },

  closeNote() {
    this.flushNote();
    this.editingSlug = null;
    if (this.hasOwnNoteInputTarget) this.ownNoteInputTarget.blur();
  },

  // Saved a moment after the typing stops, and at once when the note
  // closes or the page turns.
  noteChanged() {
    const slug = this.editingSlug;
    if (!slug) return;
    this.notesBySlug[slug] = this.ownNoteInputTarget.value.trim();
    this.pendingNote = slug;
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(() => this.flushNote(), 800);
  },

  flushNote() {
    clearTimeout(this.noteTimer);
    const slug = this.pendingNote;
    const url = this.element.dataset.presentationNotesUrl;
    this.pendingNote = null;
    if (!slug || !url) return;

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ board_note: { slug, text: this.notesBySlug[slug] || "" } }),
      keepalive: true,
    }).catch(() => {});
  },
};
